/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: RatingToasts
 * File     : RatingToasts.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: RatingToasts, composed within pages and other components.
 *   Exports: RatingToasts
 *   
 *
 * Dependencies:
 *   - ../../context/AuthContext
 *   - ../../services/api
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Import: import RatingToasts from '@/components/layout/RatingToasts';
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getRatingOpportunities } from '../../services/api';

const RatingToasts = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const fetchOps = async () => {
      try {
        const { opportunities } = await getRatingOpportunities();
        const now = Date.now();
        const eligible = (opportunities || []).filter(o => {
          const at = o.eligibleAt ? new Date(o.eligibleAt).getTime() : 0;
          return at <= now;
        });
        if (eligible && eligible.length > 0) {
          setQueue(eligible);
          setCurrent(eligible[0]);
          setOpen(true);
        } else {
          setQueue([]);
          setCurrent(null);
          setOpen(false);
        }
      } catch (_) {}
    };
    fetchOps();
    const id = setInterval(fetchOps, 60_000);
    return () => clearInterval(id);
  }, [user]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      const [, ...rest] = queue;
      setQueue(rest);
      setCurrent(rest[0] || null);
    }, 200);
  };

  const goRate = () => {
    if (current) navigate(`/results/${current.pollId}`);
    handleClose();
  };

  if (!user || !current) return null;

  return (
    <Snackbar open={open} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} autoHideDuration={8000}>
      <Alert onClose={handleClose} severity="info" sx={{ width: '100%' }} action={<Button color="inherit" size="small" onClick={goRate}>Rate</Button>}>
        You can now rate {current.movieTitle}. View results to submit your rating.
      </Alert>
    </Snackbar>
  );
};

export default RatingToasts;


